/**
 * SVGExporter - Export cut patterns as SVG
 *
 * Produces the fabrication outlines: each physical part (panel or rib)
 * with its waffle slots boolean-subtracted, laid out side by side.
 * Units are millimetres (1 sketch pixel = 1 mm).
 *
 * Replaces spShapePack.makeSVG from the Java version. Parts are nested
 * with ShapePacker.packParts (spShapePack port); layoutParts remains as
 * the simple row-layout fallback.
 */

import { subtractCutouts, unionShapes, sampleRing } from '../geometry/PolygonOps.js';
import { packParts, packPartsPaged } from './ShapePacker.js';

const GAP_MM = 10;      // Space between parts
const MARGIN_MM = 10;   // Sheet margin
const STROKE = '#ff0000'; // Laser-cut convention: hairline red
const STROKE_WIDTH = 0.2;

/**
 * Sample a shape's hole rings (closed brush strokes carry their inner
 * outline as path.holes)
 */
function sampleHoles(shape) {
    return (shape.holes || [])
        .map(hole => sampleRing(hole))
        .filter(hole => hole.length >= 3);
}

/**
 * Collect the fabrication parts: every physical plane's shapes with slot
 * cutouts subtracted. Returns a list of parts, each a list of polygons
 * ({outer, holes}) plus the source plane's material thickness in mm, the
 * source SlicePlane itself and the 2D frame its polygons live in:
 * - frame 'plane':   base panels - sketch px in the plane quaternion frame
 *                    (SlicePlane.getWorldPos: position + quaternion, px*0.01)
 * - frame 'profile': ribs - profile px in the PROFILE frame
 *                    (SlicePlane.getProfileWorldPos: position +
 *                    profileXAxis*x*0.01 + worldZ*y*0.01)
 *
 * @param {Array} basePlanes - Chair panels (skip guides)
 * @param {Array} basePlaneSketchStates - Per-plane sketch states
 * @param {Array} slicePlanes - Cross-slice rib planes (with .sketch profiles)
 * @param {Object} [options]
 * @param {boolean} [options.skipCuts=false] - Do not subtract slot cutouts
 *        (used by the "no slots" 3D-print export)
 * @param {number} [options.scale=1] - Uniform scale applied to every part
 *        coordinate (used to shrink the cut pattern onto small sheets/paper;
 *        1 = real size). Slot cutouts are subtracted BEFORE scaling so the
 *        slots stay geometrically correct, then the whole part is scaled.
 * @returns {Array<{name: string, polygons: Array, thickness: number,
 *   plane: Object, frame: string}>}
 */
export function collectParts(basePlanes, basePlaneSketchStates, slicePlanes, options = {}) {
    const { skipCuts = false, scale = 1 } = options;
    const parts = [];

    const slotRingsFor = (plane) => {
        const rings = [];
        if (skipCuts) return rings;
        if (plane.slots && plane.slots.getAll) {
            for (const slot of plane.slots.getAll()) {
                for (const ring of slot.getAllOutlines()) {
                    rings.push(ring.map(p => ({ x: p.x, y: p.y })));
                }
            }
        }
        return rings;
    };

    // Panels
    basePlanes.forEach((plane, i) => {
        if (plane.guide) return;
        const state = basePlaneSketchStates[i];
        if (!state) return;

        const shapes = [...(state.closedShapes || []), ...(state.legShapes || [])];
        const cuts = slotRingsFor(plane);

        // UNION all strokes + legs on the plane first: overlapping shapes
        // must come out as ONE cutting outline / one printable piece (the
        // same union mesh generation applies, so the cut pattern matches
        // the 3D panels). Disjoint shapes stay separate parts.
        const shapePolygons = [];
        shapes.forEach((shape) => {
            const ring = sampleRing(shape.points);
            if (ring.length < 3) return;
            shapePolygons.push({ outer: ring, holes: sampleHoles(shape) });
        });

        unionShapes(shapePolygons).forEach((polygon, j) => {
            parts.push({
                name: `panel${i + 1}-${j + 1}`,
                polygons: subtractCutouts(polygon.outer, cuts, polygon.holes),
                thickness: plane.thickness,
                plane,
                frame: 'plane'
            });
        });
    });

    // Ribs (cross-slices)
    (slicePlanes || []).forEach((plane, i) => {
        if (!plane.sketch || !plane.sketch.closedShapes) return;
        const cuts = slotRingsFor(plane);

        plane.sketch.closedShapes.forEach((shape, j) => {
            const ring = sampleRing(shape.points);
            if (ring.length < 3) return;
            parts.push({
                name: `rib${i + 1}-${j + 1}`,
                polygons: subtractCutouts(ring, cuts, sampleHoles(shape)),
                thickness: plane.thickness,
                plane,
                frame: 'profile'
            });
        });
    });

    return scale === 1 ? parts : parts.map(part => scalePart(part, scale));
}

/**
 * Return a copy of a part with every polygon coordinate multiplied by s
 * (about the origin). Shapes are laid out fresh by the packer afterwards,
 * so scaling about the origin is fine - only the size matters here.
 * @param {{polygons: Array}} part
 * @param {number} s
 */
function scalePart(part, s) {
    const scaleRing = (ring) => ring.map(p => ({ x: p.x * s, y: p.y * s }));
    return {
        ...part,
        polygons: part.polygons.map(poly => ({
            outer: scaleRing(poly.outer),
            holes: (poly.holes || []).map(scaleRing)
        }))
    };
}

/**
 * Render one polygon (outer + holes) as an SVG path with evenodd fill rule
 */
function polygonToPathData(polygon, dx, dy) {
    const ringToD = (ring) =>
        'M ' + ring.map(p => `${(p.x + dx).toFixed(2)} ${(p.y + dy).toFixed(2)}`).join(' L ') + ' Z';

    let d = ringToD(polygon.outer);
    for (const hole of polygon.holes || []) {
        d += ' ' + ringToD(hole);
    }
    return d;
}

/**
 * Simple row layout (fallback/reference; exporters use packParts)
 * @param {Array<{name, polygons}>} parts
 * @returns {{placed: Array, widthMM: number, heightMM: number}}
 */
export function layoutParts(parts) {
    // Simple row layout: parts side by side, wrapping at ~800mm
    const SHEET_WIDTH = 800;
    let cursorX = MARGIN_MM;
    let cursorY = MARGIN_MM;
    let rowHeight = 0;
    let maxX = 0;

    const placed = [];
    for (const part of parts) {
        // Part bounding box
        let minX = Infinity, minY = Infinity, maxPX = -Infinity, maxPY = -Infinity;
        for (const poly of part.polygons) {
            for (const p of poly.outer) {
                minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
                maxPX = Math.max(maxPX, p.x); maxPY = Math.max(maxPY, p.y);
            }
        }
        if (minX === Infinity) continue;

        const w = maxPX - minX;
        const h = maxPY - minY;

        if (cursorX + w > SHEET_WIDTH && cursorX > MARGIN_MM) {
            cursorX = MARGIN_MM;
            cursorY += rowHeight + GAP_MM;
            rowHeight = 0;
        }

        placed.push({ part, dx: cursorX - minX, dy: cursorY - minY });
        cursorX += w + GAP_MM;
        rowHeight = Math.max(rowHeight, h);
        maxX = Math.max(maxX, cursorX);
    }

    return {
        placed,
        widthMM: Math.ceil(maxX + MARGIN_MM),
        heightMM: Math.ceil(cursorY + rowHeight + MARGIN_MM)
    };
}

/**
 * Build the complete SVG document string.
 *
 * Without a `page` option the whole layout goes onto one sheet growing
 * downward (packParts). With `page: n` (0-based) the parts are paginated
 * with ShapePacker.packPartsPaged ({sheetWidth, sheetHeight, ...}) and
 * only that page's parts are rendered; the requested page index is
 * clamped to the available range and echoed back as `page`.
 *
 * @param {Array<{name, polygons}>} parts
 * @param {Object} [packOpts] - ShapePacker options ({sheetWidth, sheetHeight,
 *   gap, margin}) plus the optional `page` index
 * @returns {{svg: string, widthMM: number, heightMM: number, partCount: number,
 *   pageCount: number, page: number}}
 */
export function buildSVG(parts, packOpts = {}) {
    const { page = null, ...opts } = packOpts;

    let placed, widthMM, heightMM;
    let pageCount = 1;
    let pageIndex = 0;

    if (page !== null && page !== undefined) {
        const paged = packPartsPaged(parts, { gap: GAP_MM, margin: MARGIN_MM, ...opts });
        pageCount = paged.pageCount;
        pageIndex = Math.max(0, Math.min(page, pageCount - 1));
        ({ placed, widthMM, heightMM } = paged.pages[pageIndex] ||
            { placed: [], widthMM: 0, heightMM: 0 });
    } else {
        ({ placed, widthMM, heightMM } = packParts(parts, { gap: GAP_MM, margin: MARGIN_MM, ...opts }));
    }

    const pathElements = placed.map(({ part, dx, dy }) => {
        const paths = part.polygons.map(poly =>
            `    <path d="${polygonToPathData(poly, dx, dy)}" fill="none" stroke="${STROKE}" stroke-width="${STROKE_WIDTH}" fill-rule="evenodd"/>`
        ).join('\n');
        return `  <g id="${part.name}">\n${paths}\n  </g>`;
    }).join('\n');

    const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${widthMM}mm" height="${heightMM}mm" viewBox="0 0 ${widthMM} ${heightMM}">
  <!-- SketchChair.js cut pattern - units are millimetres -->
${pathElements}
</svg>
`;

    return { svg, widthMM, heightMM, partCount: placed.length, pageCount, page: pageIndex };
}
